import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dropdown,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from "@mui/joy";
import CustomModal from "components/common/CustomModal";
import { theme } from "styles/theme";
import { AddIcon } from "static/image/common/IconAssets";
import {
  convertTaskEditorImageFileToDataUrl,
  getTaskEditorBranchTargetSequences,
  getTaskEditorCategoryDefaultType,
  getTaskEditorCategoryLabel,
  getTaskEditorComponentCategory,
  getTaskEditorComponentTypeLabel,
  getTaskEditorTypographyStyleLabel,
  isSameTaskEditorBlockDropPreview,
  isSameTaskEditorComponentDropPreview,
  isTaskEditorActionComponentType,
  isTaskEditorDescriptionComponentType,
  isTaskEditorLocalImageDataUrl,
  isTaskEditorUndoShortcut,
  TASK_EDITOR_TYPOGRAPHY_STYLES,
  TaskEditorActionComponent,
  TaskEditorActionComponentType,
  TaskEditorBlock,
  TaskEditorComponentCategory,
  TaskEditorComponentLocation,
  TaskEditorComponentSection,
  TaskEditorComponentType,
  TaskEditorDeleteTarget,
  TaskEditorDescriptionComponent,
  TaskEditorDescriptionComponentType,
  TaskEditorState,
  TaskEditorTypographyStyle,
} from "./utils/taskEditorUtils";
import { useTaskEditorBlockDrag } from "./utils/useTaskEditorBlockDrag";
import { useTaskEditorComponentDrag } from "./utils/useTaskEditorComponentDrag";
import { useTaskEditorUndoHistory } from "./utils/useTaskEditorUndoHistory";
import {
  applyActionComponentTypeChange,
  applyAddBlock,
  applyAddButtonOption,
  applyAddComponent,
  applyAddTextInputField,
  applyButtonBranchOptionSequenceChange,
  applyButtonDefaultAllowMultipleChange,
  applyButtonOptionTextChange,
  applyButtonQuizIncorrectMessageChange,
  applyButtonQuizOptionCorrectToggle,
  applyDescriptionComponentTypeChange,
  applyDescriptionTextContentChange,
  applyReferencePresetTextChange,
  applyRemoveActionComponent,
  applyRemoveBlock,
  applyRemoveButtonOption,
  applyRemoveDescriptionComponent,
  applyRemoveTextInputField,
  applyTextInputFieldChange,
  applyTypographyStyleChange,
} from "./utils/taskEditorMutations";

// ─── Sx constants ─────────────────────────────────────────────────────────

const panelScrollbarSx = {
  minHeight: 0,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: "6px", opacity: 0 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&:hover::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.3)" },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "rgba(0,0,0,0.4)" },
} as const;

const taskEditorBlockCardSx = {
  position: "relative",
  p: "10px",
  borderRadius: "10px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  overflow: "hidden",
  transition:
    "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease, border-color 0.2s ease",
} as const;

const taskEditorComponentCardSx = {
  px: 0,
  py: 0.5,
  border: "none",
  borderRadius: 0,
  backgroundColor: "transparent",
  boxShadow: "none",
  transition: "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease",
} as const;

const taskEditorDashedDividerSx = {
  borderTop: "1px dashed",
  borderColor: theme.vars.palette.Border.Weak,
  height: 0,
  my: 2.5,
} as const;

const taskEditorBlockDropZoneSx = {
  position: "relative",
  height: "16px",
  mx: "8px",
  borderRadius: "12px",
  transition: "height 0.2s ease, opacity 0.2s ease, transform 0.2s ease",
} as const;

const taskEditorComponentDropZoneSx = {
  ...taskEditorBlockDropZoneSx,
  height: "12px",
  mx: 0,
} as const;

const taskEditorAddBlockButtonWrapSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  py: 1,
} as const;

const taskEditorAddBlockButtonSx = {
  minWidth: "34px",
  width: "34px",
  height: "34px",
  borderRadius: "999px",
  border: "1px dashed",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  color: theme.vars.palette.Content.Weak,
  "&:hover": {
    borderColor: theme.vars.palette.Border.Strong,
    backgroundColor: theme.vars.palette.Background.Level2,
  },
} as const;

const taskEditorDeleteTargetLabelMap = {
  block: "블록",
  component: "컴포넌트",
} as const;

const taskEditorSelectSx = {
  minHeight: "28px",
  px: 1,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  boxShadow: "none",
  color: theme.vars.palette.Content.Strong,
  fontSize: "12px",
  fontWeight: 600,
  "--Select-indicatorColor": theme.vars.palette.Content.Weak,
  "--Select-placeholderOpacity": 1,
  "--Select-decoratorColor": theme.vars.palette.Content.Weak,
  transition:
    "border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease",
  "&:hover": {
    borderColor: theme.vars.palette.Border.Weak,
    backgroundColor: theme.vars.palette.Surface.Forward,
    transform: "translateY(-1px)",
  },
  "&:focus-within": {
    borderColor: theme.vars.palette.primary.soft,
    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.12)",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.vars.palette.Background.Level1,
    color: theme.vars.palette.Content.Weak,
    boxShadow: "none",
  },
} as const;

const taskEditorSelectButtonSx = {
  pl: 1.5,
  pr: 1.25,
} as const;

const taskEditorSubtypeSelectSx = {
  minHeight: "28px",
  px: 1,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: "transparent",
  boxShadow: "none",
  color: theme.vars.palette.Content.Medium,
  fontSize: "12px",
  fontWeight: 600,
  "--Select-indicatorColor": theme.vars.palette.Content.Weak,
  "&:hover": {
    borderColor: theme.vars.palette.Border.Weak,
    backgroundColor: theme.vars.palette.Background.Level1,
  },
} as const;

const taskEditorSubtypeSelectButtonSx = {
  pl: 1.5,
  pr: 1.25,
} as const;

const taskEditorSelectListboxSx = {
  p: 0.25,
  border: "1px solid",
  borderColor: theme.vars.palette.Border.SuperWeak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  borderRadius: "10px",
  boxShadow:
    "0 8px 24px -12px var(--mono-colors-with-opacity-pure-black-20, rgba(0, 0, 0, 0.20))",
  "--List-gap": "2px",
  "--ListItem-radius": "8px",
  "& .MuiOption-root, & .MuiMenuItem-root": {
    minHeight: "32px",
    px: 1,
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: theme.vars.palette.Content.Medium,
  },
  "& .MuiOption-root:hover, & .MuiMenuItem-root:hover": {
    backgroundColor: theme.vars.palette.Background.Level1,
  },
  "& .MuiOption-root[aria-selected='true']": {
    backgroundColor: theme.vars.palette.primary.soft,
    color: theme.vars.palette.primary.solid,
  },
} as const;

const taskEditorCompactFieldLabelSx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: 1.3,
} as const;

const taskEditorButtonGroupEditorSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
} as const;

const taskEditorCompactFieldSx = {
  minWidth: 0,
} as const;

const taskEditorButtonGroupOptionListSx = {
  display: "grid",
  gap: 0.75,
  minWidth: 0,
} as const;

const taskEditorButtonGroupGuideTriggerSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
} as const;

const taskEditorButtonGroupGuideIconSx = {
  minWidth: "20px",
  minHeight: "20px",
  width: "20px",
  height: "20px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  color: theme.vars.palette.Content.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  "&:hover": { backgroundColor: theme.vars.palette.Background.Level2 },
} as const;

const taskEditorButtonGroupGuideTooltipSx = {
  whiteSpace: "pre-line",
  fontSize: "12px",
  lineHeight: 1.5,
  color: "#FFFFFF",
  maxWidth: "300px",
} as const;

const taskEditorButtonGroupGuideTooltipSlotSx = {
  backgroundColor: "rgba(15, 23, 42, 0.96)",
  color: "#FFFFFF",
  boxShadow: "0 10px 28px rgba(2, 6, 23, 0.45)",
  borderRadius: "10px",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  px: 1.25,
  py: 1,
  maxWidth: "340px",
} as const;

const taskEditorButtonGroupGuideTooltipArrowSx = {
  "&::before": {
    borderTopColor: "rgba(15, 23, 42, 0.96)",
    borderRightColor: "rgba(15, 23, 42, 0.96)",
    boxShadow: "none",
  },
} as const;

const TASK_EDITOR_BUTTON_BRANCH_GUIDE_TEXT = [
  "시작 블록: 이 버튼 옵션에서 처음 보여줄 블록 sequence 입니다.",
  "종료 블록: 이 버튼 옵션에서 마지막으로 보여줄 블록 sequence 입니다.",
  "다시 시작 블록: 종료 후 이어서 이동할 블록 sequence 입니다.",
  "예: A(4~7, 다시 시작 12), B(8~10, 다시 시작 12), C(11, 다시 시작 12)",
].join("\n");

const taskEditorButtonGroupOptionRowSx = {
  display: "grid",
  gap: 0.5,
  minWidth: 0,
} as const;

const taskEditorButtonGroupBranchControlsSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
  gap: 0.5,
  minWidth: 0,
} as const;

const taskEditorButtonGroupBranchFieldSx = {
  minWidth: 0,
  display: "grid",
  gap: 0.25,
} as const;

const taskEditorButtonGroupBranchFieldLabelSx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "10px",
  fontWeight: 700,
  lineHeight: 1.2,
} as const;

const taskEditorButtonGroupSummarySx = {
  color: theme.vars.palette.Content.Weak,
  fontSize: "10px",
  lineHeight: 1.35,
  px: 0.25,
} as const;

const taskEditorButtonGroupOptionChipSx = {
  minHeight: "28px",
  borderRadius: "999px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
  px: 0.75,
  py: 0.25,
} as const;

const taskEditorButtonGroupOptionInputSx = {
  minWidth: 0,
  width: "120px",
  "--Input-radius": "999px",
  "--Input-minHeight": "24px",
  "--Input-paddingInline": "8px",
  "--Input-decoratorChildHeight": "16px",
  "& .MuiInput-input": { fontSize: "12px", fontWeight: 600 },
} as const;

const taskEditorButtonGroupBranchSelectSx = {
  ...taskEditorSelectSx,
} as const;

const taskEditorInlineAddButtonSx = {
  fontSize: "12px",
  fontWeight: 600,
  minHeight: "28px",
  borderRadius: "999px",
  px: 1.25,
  // grid item 이라 기본적으로 행 너비를 가득 채우는데, 일반 옵션 chip 처럼
  // 자기 컨텐츠 폭만 차지하도록 좌측 정렬.
  justifySelf: "start",
} as const;

const taskEditorImagePreviewSx = {
  width: "100%",
  maxHeight: "160px",
  objectFit: "contain",
  borderRadius: "8px",
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Background.Level1,
} as const;

// ─── 카테고리 매핑 ────────────────────────────────────────────────────────

const DESCRIPTION_CATEGORIES: TaskEditorComponentCategory[] = [
  "text",
  "image",
  "reference",
];
const ACTION_CATEGORIES: TaskEditorComponentCategory[] = [
  "text_input",
  "button",
];

function getSectionCategories(
  section: TaskEditorComponentSection
): TaskEditorComponentCategory[] {
  return section === "description" ? DESCRIPTION_CATEGORIES : ACTION_CATEGORIES;
}

const REFERENCE_SUBTYPES: TaskEditorDescriptionComponentType[] = [
  "reference_automatic_thought",
  "reference_coach_preset",
];

const BUTTON_SUBTYPES: TaskEditorActionComponentType[] = [
  "button_branch",
  "button_default",
  "button_quiz",
];

// ─── Props & Component ─────────────────────────────────────────────────────

export interface TaskEditorWorkspaceProps {
  editorState: TaskEditorState;
  onChangeEditorState: (next: TaskEditorState) => void;
}

export default function TaskEditorWorkspace({
  editorState,
  onChangeEditorState,
}: TaskEditorWorkspaceProps): JSX.Element {
  const [blocks, setBlocksInternal] = useState<TaskEditorBlock[]>(
    editorState.blocks
  );

  // 외부 prop 동기화: editorState.blocks 가 다른 ref 로 바뀌면 내부 state 도 교체
  const prevBlocksRef = useRef(editorState.blocks);
  useEffect(() => {
    if (editorState.blocks !== prevBlocksRef.current) {
      prevBlocksRef.current = editorState.blocks;
      setBlocksInternal(editorState.blocks);
    }
  }, [editorState.blocks]);

  // 핸들러가 매 render 마다 새 closure 가 되지 않도록, 최신 값을 ref 로 노출.
  // 핸들러는 useCallback([] deps) 로 안정화 후 ref 를 통해 최신 데이터를 읽음.
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const onChangeEditorStateRef = useRef(onChangeEditorState);
  onChangeEditorStateRef.current = onChangeEditorState;

  // setBlocks: setter identity 는 React 가 보장 — 그대로 안정.
  const setBlocks: React.Dispatch<React.SetStateAction<TaskEditorBlock[]>> =
    setBlocksInternal;

  // 내부 blocks 변경 → 부모로 전파
  useEffect(() => {
    if (blocks === prevBlocksRef.current) return;
    prevBlocksRef.current = blocks;
    onChangeEditorStateRef.current({
      ...editorStateRef.current,
      blocks,
    });
  }, [blocks]);

  const [pendingDeleteTarget, setPendingDeleteTarget] =
    useState<TaskEditorDeleteTarget | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [jsonInspect, setJsonInspect] = useState<{
    block: TaskEditorBlock;
    sequence: number;
  } | null>(null);

  const pendingDeleteTargetLabel = pendingDeleteTarget
    ? taskEditorDeleteTargetLabelMap[pendingDeleteTarget.type]
    : undefined;

  // ─── undo ────────────────────────────────────────────────────────────────
  // getCurrentState / applyState 를 stable useCallback 으로 만들어
  // undoHistory 가 반환하는 pushSnapshot / pushSnapshotForTextEdit 도 stable 하게.

  const getCurrentStateStable = useCallback(
    () => ({ ...editorStateRef.current, blocks: blocksRef.current }),
    []
  );
  const applyStateStable = useCallback((state: TaskEditorState) => {
    prevBlocksRef.current = state.blocks;
    setBlocksInternal(state.blocks);
    onChangeEditorStateRef.current(state);
  }, []);

  const undoHistory = useTaskEditorUndoHistory({
    getCurrentState: getCurrentStateStable,
    applyState: applyStateStable,
  });
  const {
    pushSnapshot,
    pushSnapshotForTextEdit,
    resetTextEditGrouping,
    undo,
    hasHistory,
  } = undoHistory;

  useEffect(() => {
    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (
        !hasHistory ||
        event.defaultPrevented ||
        event.repeat ||
        !isTaskEditorUndoShortcut(event)
      ) {
        return;
      }

      event.preventDefault();
      undo();
    };

    window.addEventListener("keydown", handleWindowKeyDown);
    return () => window.removeEventListener("keydown", handleWindowKeyDown);
  }, [hasHistory, undo]);

  // ─── drag ────────────────────────────────────────────────────────────────

  const dragCancelHolder = useRef({
    block: () => {},
    component: () => {},
  });

  const onDragReorder = useCallback(() => {
    resetTextEditGrouping();
    pushSnapshot();
  }, [resetTextEditGrouping, pushSnapshot]);

  const cancelComponentDragFromBlock = useCallback(
    () => dragCancelHolder.current.component(),
    []
  );
  const cancelBlockDragFromComponent = useCallback(
    () => dragCancelHolder.current.block(),
    []
  );

  const {
    draggedBlockIndex,
    blockDropPreview,
    handleBlockDragStart,
    handleBlockDragOver,
    handleBlockDrop,
    handleBlockDragEnd,
    handleBlockCardDragOver,
    handleBlockCardDrop,
    cancelDrag: cancelBlockDrag,
  } = useTaskEditorBlockDrag({
    blocks,
    setBlocks,
    onBeforeReorder: onDragReorder,
    cancelOtherDrag: cancelComponentDragFromBlock,
  });

  const {
    draggedComponent,
    componentDropPreview,
    handleComponentDragStart,
    handleComponentDragOver,
    handleComponentDrop,
    handleComponentDragEnd,
    handleComponentCardDragOver,
    handleComponentCardDrop,
    cancelDrag: cancelComponentDrag,
  } = useTaskEditorComponentDrag({
    blocks,
    setBlocks,
    onBeforeReorder: onDragReorder,
    cancelOtherDrag: cancelBlockDragFromComponent,
  });

  dragCancelHolder.current.block = cancelBlockDrag;
  dragCancelHolder.current.component = cancelComponentDrag;

  // ─── handlers (모두 stable — blocksRef 를 통해 최신 blocks 읽음) ────────────

  const commit = useCallback(
    (next: TaskEditorBlock[]) => {
      if (next === blocksRef.current) return;
      resetTextEditGrouping();
      pushSnapshot();
      setBlocks(next);
    },
    [resetTextEditGrouping, pushSnapshot, setBlocks]
  );

  const commitTextEdit = useCallback(
    (groupKey: string, next: TaskEditorBlock[]) => {
      if (next === blocksRef.current) return;
      pushSnapshotForTextEdit(groupKey);
      setBlocks(next);
    },
    [pushSnapshotForTextEdit, setBlocks]
  );

  // ─── 새 블록 하이라이트 + 스크롤 ──────────────────────────────────────────
  // handleAddBlock 호출 시 정확한 insertionIndex 를 기억해서 그 블록을 잠시
  // 강조하고 화면을 그쪽으로 이동. + 버튼이 블록 사이에 있는 경우에도 올바른
  // 위치를 가리키도록 한다.
  const blockCardRefs = useRef<Map<number, HTMLElement>>(new Map());
  const [highlightedBlockIndex, setHighlightedBlockIndex] = useState<
    number | null
  >(null);

  const handleAddBlock = useCallback(
    (insertionIndex?: number) => {
      const current = blocksRef.current;
      const idx =
        typeof insertionIndex === "number"
          ? Math.max(0, Math.min(insertionIndex, current.length))
          : current.length;
      commit(applyAddBlock(current, insertionIndex));
      setHighlightedBlockIndex(idx);
    },
    [commit]
  );

  // 하이라이트 자동 해제
  useEffect(() => {
    if (highlightedBlockIndex === null) return;
    const t = window.setTimeout(() => setHighlightedBlockIndex(null), 1500);
    return () => window.clearTimeout(t);
  }, [highlightedBlockIndex]);

  // 하이라이트된 블록으로 스크롤 (DOM 업데이트 후). 화면 가운데에 자리잡도록
  // block: "center" — 새로 추가된 블록이 자연스럽게 시야 가운데로 올라온다.
  useEffect(() => {
    if (highlightedBlockIndex === null) return;
    const node = blockCardRefs.current.get(highlightedBlockIndex);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightedBlockIndex]);

  const handleRemoveBlock = useCallback(
    (blockIndex: number) =>
      commit(applyRemoveBlock(blocksRef.current, blockIndex)),
    [commit]
  );

  const handleAddComponent = useCallback(
    (blockIndex: number, type: TaskEditorComponentType) =>
      commit(applyAddComponent(blocksRef.current, blockIndex, type)),
    [commit]
  );

  const handleRemoveComponent = useCallback(
    (location: TaskEditorComponentLocation) => {
      if (location.section === "description") {
        commit(
          applyRemoveDescriptionComponent(
            blocksRef.current,
            location.blockIndex,
            location.descriptionIndex
          )
        );
      } else {
        commit(
          applyRemoveActionComponent(blocksRef.current, location.blockIndex)
        );
      }
    },
    [commit]
  );

  const handleDescriptionComponentTypeChange = useCallback(
    (
      blockIndex: number,
      descriptionIndex: number,
      nextType: TaskEditorDescriptionComponentType
    ) =>
      commit(
        applyDescriptionComponentTypeChange(
          blocksRef.current,
          blockIndex,
          descriptionIndex,
          nextType
        )
      ),
    [commit]
  );

  const handleActionComponentTypeChange = useCallback(
    (blockIndex: number, nextType: TaskEditorActionComponentType) =>
      commit(
        applyActionComponentTypeChange(blocksRef.current, blockIndex, nextType)
      ),
    [commit]
  );

  const handleDescriptionContentChange = useCallback(
    (
      blockIndex: number,
      descriptionIndex: number,
      content: string,
      groupKey: string
    ) =>
      commitTextEdit(
        groupKey,
        applyDescriptionTextContentChange(
          blocksRef.current,
          blockIndex,
          descriptionIndex,
          content
        )
      ),
    [commitTextEdit]
  );

  const handleTypographyStyleChange = useCallback(
    (
      blockIndex: number,
      descriptionIndex: number,
      style: TaskEditorTypographyStyle
    ) =>
      commit(
        applyTypographyStyleChange(
          blocksRef.current,
          blockIndex,
          descriptionIndex,
          style
        )
      ),
    [commit]
  );

  const handleReferencePresetTextChange = useCallback(
    (blockIndex: number, descriptionIndex: number, text: string) =>
      commitTextEdit(
        `reference-preset-text:${blockIndex}:${descriptionIndex}`,
        applyReferencePresetTextChange(
          blocksRef.current,
          blockIndex,
          descriptionIndex,
          text
        )
      ),
    [commitTextEdit]
  );

  const handleAddTextInputField = useCallback(
    (blockIndex: number) =>
      commit(applyAddTextInputField(blocksRef.current, blockIndex)),
    [commit]
  );

  const handleRemoveTextInputField = useCallback(
    (blockIndex: number, fieldIndex: number) =>
      commit(
        applyRemoveTextInputField(blocksRef.current, blockIndex, fieldIndex)
      ),
    [commit]
  );

  const handleTextInputFieldChange = useCallback(
    (
      blockIndex: number,
      fieldIndex: number,
      key: "label" | "placeholder" | "help_text",
      value: string
    ) =>
      commitTextEdit(
        `text-input-field:${blockIndex}:${fieldIndex}:${key}`,
        applyTextInputFieldChange(
          blocksRef.current,
          blockIndex,
          fieldIndex,
          key,
          value
        )
      ),
    [commitTextEdit]
  );

  const handleAddButtonOption = useCallback(
    (blockIndex: number) =>
      commit(applyAddButtonOption(blocksRef.current, blockIndex)),
    [commit]
  );

  const handleRemoveButtonOption = useCallback(
    (blockIndex: number, optionIndex: number) =>
      commit(
        applyRemoveButtonOption(blocksRef.current, blockIndex, optionIndex)
      ),
    [commit]
  );

  const handleButtonOptionTextChange = useCallback(
    (blockIndex: number, optionIndex: number, text: string) =>
      commitTextEdit(
        `button-option-text:${blockIndex}:${optionIndex}`,
        applyButtonOptionTextChange(
          blocksRef.current,
          blockIndex,
          optionIndex,
          text
        )
      ),
    [commitTextEdit]
  );

  const handleButtonQuizOptionCorrectToggle = useCallback(
    (blockIndex: number, optionIndex: number, isCorrect: boolean) =>
      commit(
        applyButtonQuizOptionCorrectToggle(
          blocksRef.current,
          blockIndex,
          optionIndex,
          isCorrect
        )
      ),
    [commit]
  );

  const handleButtonBranchOptionSequenceChange = useCallback(
    (
      blockIndex: number,
      optionIndex: number,
      key: "show_from" | "show_to" | "restart_from" | "hide_from",
      value: number | undefined
    ) =>
      commit(
        applyButtonBranchOptionSequenceChange(
          blocksRef.current,
          blockIndex,
          optionIndex,
          key,
          value
        )
      ),
    [commit]
  );

  const handleButtonQuizIncorrectMessageChange = useCallback(
    (blockIndex: number, message: string) =>
      commitTextEdit(
        `button-quiz-incorrect-message:${blockIndex}`,
        applyButtonQuizIncorrectMessageChange(
          blocksRef.current,
          blockIndex,
          message
        )
      ),
    [commitTextEdit]
  );

  const handleButtonDefaultAllowMultipleChange = useCallback(
    (blockIndex: number, allow: boolean) =>
      commit(
        applyButtonDefaultAllowMultipleChange(
          blocksRef.current,
          blockIndex,
          allow
        )
      ),
    [commit]
  );

  const handleImageFileChange = useCallback(
    async (blockIndex: number, descriptionIndex: number, file: File) => {
      try {
        const imageDataUrl = await convertTaskEditorImageFileToDataUrl(file);
        const currentBlocks = blocksRef.current;
        const currentBlock = currentBlocks[blockIndex];
        const currentComponent = currentBlock?.description[descriptionIndex];

        if (
          !currentBlock ||
          !currentComponent ||
          currentComponent.type !== "image" ||
          currentComponent.content === imageDataUrl
        ) {
          return;
        }

        commit(
          applyDescriptionTextContentChange(
            currentBlocks,
            blockIndex,
            descriptionIndex,
            imageDataUrl
          )
        );
      } catch {
        // 이미지 업로드 실패는 조용히 처리
      }
    },
    [commit]
  );

  const handleOpenDeleteTargetModal = useCallback(
    (target: TaskEditorDeleteTarget) => {
      setPendingDeleteTarget(target);
    },
    []
  );

  const handleCloseDeleteTargetModal = useCallback(() => {
    setPendingDeleteTarget(null);
  }, []);

  const handleConfirmDeleteTarget = useCallback(() => {
    if (!pendingDeleteTarget) return;

    if (pendingDeleteTarget.type === "block") {
      handleRemoveBlock(pendingDeleteTarget.blockIndex);
    } else {
      handleRemoveComponent(pendingDeleteTarget.location);
    }

    setPendingDeleteTarget(null);
  }, [pendingDeleteTarget, handleRemoveBlock, handleRemoveComponent]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <Box
        sx={{
          ...panelScrollbarSx,
          px: "12px",
          py: "12px",
          height: "100%",
          minHeight: 0,
          fontSize: "13px",
          "& input": { fontSize: "13px" },
          "& textarea": { fontSize: "13px" },
        }}
        className="scrollbar-hide"
        data-testid="task-editor-block-panel"
      >
        <Stack
          spacing={2}
          sx={{ maxWidth: "660px", mx: "auto", width: "100%" }}
        >
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ mb: -1 }}
          >
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              startDecorator={
                <span
                  style={{ fontSize: "16px" }}
                  className="material-symbols-rounded"
                >
                  help_outline
                </span>
              }
              data-testid="task-editor-help-trigger"
              onClick={() => setIsHelpOpen(true)}
              sx={{
                minHeight: 0,
                py: 0.25,
                px: 0.75,
                fontSize: "12px",
                fontWeight: 600,
                color: theme.vars.palette.Content.Medium,
              }}
            >
              도움말
            </Button>
          </Stack>

          <Stack spacing={0}>
            {blocks.length > 0
              ? (() => {
                  const isPreviewActive = isSameTaskEditorBlockDropPreview(
                    blockDropPreview,
                    { insertionIndex: 0 }
                  );
                  return (
                    <Box
                      key="drop-zone-before-first"
                      data-testid="task-editor-block-drop-zone"
                      data-drop-preview={
                        isPreviewActive ? "active" : "inactive"
                      }
                      onDragOver={(event) =>
                        handleBlockDragOver(event, { insertionIndex: 0 })
                      }
                      onDrop={(event) =>
                        handleBlockDrop(event, { insertionIndex: 0 })
                      }
                      sx={{
                        ...taskEditorBlockDropZoneSx,
                        height: isPreviewActive
                          ? "28px"
                          : taskEditorBlockDropZoneSx.height,
                        opacity: draggedBlockIndex !== null ? 1 : 0,
                        transform: isPreviewActive
                          ? "scaleY(1)"
                          : "scaleY(0.92)",
                      }}
                    />
                  );
                })()
              : null}

            {blocks.map((block, blockIndex) => {
              const isDraggedBlock = draggedBlockIndex === blockIndex;
              const isAfterPreviewActive = isSameTaskEditorBlockDropPreview(
                blockDropPreview,
                { insertionIndex: blockIndex + 1 }
              );

              const isHighlighted = highlightedBlockIndex === blockIndex;

              return (
                <React.Fragment key={`block-${blockIndex}`}>
                  <Sheet
                    ref={(el: HTMLElement | null) => {
                      if (el) blockCardRefs.current.set(blockIndex, el);
                      else blockCardRefs.current.delete(blockIndex);
                    }}
                    sx={{
                      ...taskEditorBlockCardSx,
                      opacity: isDraggedBlock ? 0.96 : 1,
                      transform: isDraggedBlock
                        ? "translateY(-10px) scale(1.015)"
                        : "none",
                      // 새 블록 하이라이트: 카드 전체에 매우 옅은 파랑 톤만 살짝
                      // 입혀 자연스럽게 강조. 1.5초 뒤 자동 해제되며 천천히 fade.
                      backgroundColor: isHighlighted
                        ? "rgba(15, 85, 167, 0.04)"
                        : taskEditorBlockCardSx.backgroundColor,
                      boxShadow: isDraggedBlock
                        ? "0 28px 64px rgba(15, 23, 42, 0.16)"
                        : taskEditorBlockCardSx.boxShadow,
                      borderColor: isHighlighted
                        ? "rgba(15, 85, 167, 0.10)"
                        : isDraggedBlock
                          ? theme.vars.palette.primary.soft
                          : taskEditorBlockCardSx.borderColor,
                      transition:
                        "background-color 0.7s ease, border-color 0.7s ease, transform 0.25s ease",
                      zIndex: isDraggedBlock ? 2 : 1,
                      mb: 0,
                    }}
                    data-testid="task-editor-block-card"
                    data-block-index={blockIndex}
                    data-dragging={isDraggedBlock ? "true" : "false"}
                    data-drop-preview="none"
                    onDragOver={(event) =>
                      handleBlockCardDragOver(event, blockIndex)
                    }
                    onDrop={(event) => handleBlockCardDrop(event, blockIndex)}
                  >
                    <Stack spacing={1.75} sx={{ position: "relative" }}>
                      <BlockHeader
                        blockIndex={blockIndex}
                        isDragging={isDraggedBlock}
                        canRemove={blocks.length > 1}
                        onDragStart={(event) =>
                          handleBlockDragStart(event, blockIndex)
                        }
                        onDragEnd={handleBlockDragEnd}
                        onOpenJson={() =>
                          setJsonInspect({
                            block,
                            sequence: blockIndex + 1,
                          })
                        }
                        onDelete={() =>
                          handleOpenDeleteTargetModal({
                            type: "block",
                            blockIndex,
                          })
                        }
                      />

                      <BlockBody
                        block={block}
                        blockIndex={blockIndex}
                        blockCount={blocks.length}
                        draggedComponent={draggedComponent}
                        componentDropPreview={componentDropPreview}
                        onComponentDragStart={handleComponentDragStart}
                        onComponentDragEnd={handleComponentDragEnd}
                        onComponentDragOver={handleComponentDragOver}
                        onComponentDrop={handleComponentDrop}
                        onComponentCardDragOver={handleComponentCardDragOver}
                        onComponentCardDrop={handleComponentCardDrop}
                        onAddComponent={handleAddComponent}
                        onRemoveComponent={(location) =>
                          handleOpenDeleteTargetModal({
                            type: "component",
                            location,
                          })
                        }
                        onDescriptionComponentTypeChange={
                          handleDescriptionComponentTypeChange
                        }
                        onActionComponentTypeChange={
                          handleActionComponentTypeChange
                        }
                        onDescriptionContentChange={
                          handleDescriptionContentChange
                        }
                        onTypographyStyleChange={handleTypographyStyleChange}
                        onReferencePresetTextChange={
                          handleReferencePresetTextChange
                        }
                        onAddTextInputField={handleAddTextInputField}
                        onRemoveTextInputField={handleRemoveTextInputField}
                        onTextInputFieldChange={handleTextInputFieldChange}
                        onImageFileChange={handleImageFileChange}
                        onAddButtonOption={handleAddButtonOption}
                        onRemoveButtonOption={handleRemoveButtonOption}
                        onButtonOptionTextChange={handleButtonOptionTextChange}
                        onButtonQuizOptionCorrectToggle={
                          handleButtonQuizOptionCorrectToggle
                        }
                        onButtonBranchOptionSequenceChange={
                          handleButtonBranchOptionSequenceChange
                        }
                        onButtonQuizIncorrectMessageChange={
                          handleButtonQuizIncorrectMessageChange
                        }
                        onButtonDefaultAllowMultipleChange={
                          handleButtonDefaultAllowMultipleChange
                        }
                        onTextEditBlur={undoHistory.resetTextEditGrouping}
                      />
                    </Stack>
                  </Sheet>

                  <Stack sx={taskEditorAddBlockButtonWrapSx}>
                    <IconButton
                      aria-label="블록 사이에 추가"
                      data-testid="task-editor-add-block-between"
                      onClick={() => handleAddBlock(blockIndex + 1)}
                      sx={taskEditorAddBlockButtonSx}
                    >
                      <AddIcon size={16} />
                    </IconButton>
                    <Box
                      data-testid="task-editor-block-drop-zone"
                      data-drop-preview={
                        isAfterPreviewActive ? "active" : "inactive"
                      }
                      onDragOver={(event) =>
                        handleBlockDragOver(event, {
                          insertionIndex: blockIndex + 1,
                        })
                      }
                      onDrop={(event) =>
                        handleBlockDrop(event, {
                          insertionIndex: blockIndex + 1,
                        })
                      }
                      sx={{
                        ...taskEditorBlockDropZoneSx,
                        height:
                          draggedBlockIndex !== null
                            ? isAfterPreviewActive
                              ? "28px"
                              : taskEditorBlockDropZoneSx.height
                            : "0px",
                        opacity: draggedBlockIndex !== null ? 1 : 0,
                        transform: isAfterPreviewActive
                          ? "scaleY(1)"
                          : "scaleY(0.92)",
                      }}
                    />
                  </Stack>
                </React.Fragment>
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <CustomModal
        open={Boolean(pendingDeleteTarget)}
        onClose={handleCloseDeleteTargetModal}
        title={
          pendingDeleteTargetLabel
            ? `${pendingDeleteTargetLabel}을 삭제할까요?`
            : "삭제할까요?"
        }
        subtitle={
          pendingDeleteTargetLabel
            ? `삭제 대상 type: ${pendingDeleteTargetLabel}`
            : undefined
        }
        actions={[
          {
            label: "삭제하기",
            variant: "Solid",
            color: "danger",
            onClick: handleConfirmDeleteTarget,
          },
          {
            label: "취소",
            variant: "Plain",
            onClick: handleCloseDeleteTargetModal,
          },
        ]}
      />

      <TaskEditorHelpModal
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
      />

      <TaskEditorBlockJsonModal
        target={jsonInspect}
        onClose={() => setJsonInspect(null)}
      />
    </>
  );
}

// ─── 블록 헤더 ────────────────────────────────────────────────────────────

interface BlockHeaderProps {
  blockIndex: number;
  isDragging: boolean;
  canRemove: boolean;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onOpenJson: () => void;
  onDelete: () => void;
}

function BlockHeader({
  blockIndex,
  isDragging,
  canRemove,
  onDragStart,
  onDragEnd,
  onOpenJson,
  onDelete,
}: BlockHeaderProps) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <IconButton
          size="sm"
          component="div"
          role="button"
          tabIndex={0}
          aria-label={`블록 ${blockIndex + 1} 이동`}
          data-testid={`task-editor-drag-block-${blockIndex}`}
          draggable
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          sx={{
            cursor: isDragging ? "grabbing" : "grab",
          }}
        >
          <span
            style={{ fontSize: "18px" }}
            className="material-symbols-rounded"
          >
            drag_indicator
          </span>
        </IconButton>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {`블록 ${blockIndex + 1}`}
        </Typography>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <IconButton
          size="sm"
          aria-label="블록 JSON 보기"
          data-testid={`task-editor-view-block-json-${blockIndex}`}
          onClick={onOpenJson}
        >
          <span
            style={{ fontSize: "18px" }}
            className="material-symbols-rounded"
          >
            data_object
          </span>
        </IconButton>
        {canRemove ? (
          <IconButton
            size="sm"
            aria-label="블록 삭제"
            data-testid={`task-editor-delete-block-${blockIndex}`}
            onClick={onDelete}
          >
            <span
              style={{ fontSize: "16px" }}
              className="material-symbols-rounded"
            >
              close
            </span>
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
}

// ─── 블록 본문 ────────────────────────────────────────────────────────────

interface BlockBodyProps {
  block: TaskEditorBlock;
  blockIndex: number;
  blockCount: number;
  draggedComponent: { blockIndex: number; descriptionIndex: number } | null;
  componentDropPreview: { blockIndex: number; insertionIndex: number } | null;
  onComponentDragStart: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    descriptionIndex: number
  ) => void;
  onComponentDragEnd: () => void;
  onComponentDragOver: (
    event: React.DragEvent<HTMLElement>,
    preview: { blockIndex: number; insertionIndex: number }
  ) => void;
  onComponentDrop: (
    event: React.DragEvent<HTMLElement>,
    preview: { blockIndex: number; insertionIndex: number }
  ) => void;
  onComponentCardDragOver: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    targetDescriptionIndex: number
  ) => void;
  onComponentCardDrop: (
    event: React.DragEvent<HTMLElement>,
    blockIndex: number,
    targetDescriptionIndex: number
  ) => void;
  onAddComponent: (blockIndex: number, type: TaskEditorComponentType) => void;
  onRemoveComponent: (location: TaskEditorComponentLocation) => void;
  onDescriptionComponentTypeChange: (
    blockIndex: number,
    descriptionIndex: number,
    nextType: TaskEditorDescriptionComponentType
  ) => void;
  onActionComponentTypeChange: (
    blockIndex: number,
    nextType: TaskEditorActionComponentType
  ) => void;
  onDescriptionContentChange: (
    blockIndex: number,
    descriptionIndex: number,
    content: string,
    groupKey: string
  ) => void;
  onTypographyStyleChange: (
    blockIndex: number,
    descriptionIndex: number,
    style: TaskEditorTypographyStyle
  ) => void;
  onReferencePresetTextChange: (
    blockIndex: number,
    descriptionIndex: number,
    text: string
  ) => void;
  onAddTextInputField: (blockIndex: number) => void;
  onRemoveTextInputField: (blockIndex: number, fieldIndex: number) => void;
  onTextInputFieldChange: (
    blockIndex: number,
    fieldIndex: number,
    key: "label" | "placeholder" | "help_text",
    value: string
  ) => void;
  onImageFileChange: (
    blockIndex: number,
    descriptionIndex: number,
    file: File
  ) => Promise<void>;
  onAddButtonOption: (blockIndex: number) => void;
  onRemoveButtonOption: (blockIndex: number, optionIndex: number) => void;
  onButtonOptionTextChange: (
    blockIndex: number,
    optionIndex: number,
    text: string
  ) => void;
  onButtonQuizOptionCorrectToggle: (
    blockIndex: number,
    optionIndex: number,
    isCorrect: boolean
  ) => void;
  onButtonBranchOptionSequenceChange: (
    blockIndex: number,
    optionIndex: number,
    key: "show_from" | "show_to" | "restart_from" | "hide_from",
    value: number | undefined
  ) => void;
  onButtonQuizIncorrectMessageChange: (
    blockIndex: number,
    message: string
  ) => void;
  onButtonDefaultAllowMultipleChange: (
    blockIndex: number,
    allow: boolean
  ) => void;
  onTextEditBlur: () => void;
}

const BlockBody = React.memo(BlockBodyImpl, blockBodyPropsEqual);

function blockBodyPropsEqual(
  prev: BlockBodyProps,
  next: BlockBodyProps
): boolean {
  // 핸들러 ref 는 비교 대상에서 제외 — 모두 useCallback([], blocksRef 기반) 으로 안정.
  return (
    prev.block === next.block &&
    prev.blockIndex === next.blockIndex &&
    prev.blockCount === next.blockCount &&
    prev.draggedComponent === next.draggedComponent &&
    prev.componentDropPreview === next.componentDropPreview
  );
}

function BlockBodyImpl(props: BlockBodyProps) {
  const {
    block,
    blockIndex,
    draggedComponent,
    componentDropPreview,
    onComponentDragOver,
    onComponentDrop,
    onAddComponent,
  } = props;

  const hasText = block.description.some((c) => c.type === "text");
  const isComponentDragActive = draggedComponent?.blockIndex === blockIndex;
  const descCount = block.description.length;

  const isBeforePreviewActive = isSameTaskEditorComponentDropPreview(
    componentDropPreview,
    { blockIndex, insertionIndex: 0 }
  );

  const renderAddMenu = (section: TaskEditorComponentSection) => {
    if (section === "action" && block.action !== null) {
      return null;
    }
    const categories = getSectionCategories(section);
    return (
      <Dropdown>
        <MenuButton
          size="sm"
          variant="outlined"
          color="neutral"
          sx={{
            ...taskEditorSelectSx,
            minWidth: 0,
            px: 1.25,
          }}
          data-testid={`task-editor-add-component-${section}-${blockIndex}`}
        >
          + 추가
        </MenuButton>
        <Menu placement="bottom-start" sx={taskEditorSelectListboxSx}>
          {categories.map((category) => {
            const defaultType = getTaskEditorCategoryDefaultType(category);
            return (
              <MenuItem
                key={category}
                data-testid={`task-editor-add-component-${section}-${blockIndex}-${category}`}
                onClick={() => onAddComponent(blockIndex, defaultType)}
              >
                {getTaskEditorCategoryLabel(category)}
              </MenuItem>
            );
          })}
        </Menu>
      </Dropdown>
    );
  };

  const renderSectionHeader = (section: TaskEditorComponentSection) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ minHeight: "28px", mb: 0.75 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.25}>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Medium,
            fontWeight: 700,
            fontSize: "12px",
            letterSpacing: "0.02em",
            lineHeight: 1.4,
          }}
        >
          {section === "description" ? "설명" : "유저 액션"}
        </Typography>
        {section === "description" ? (
          <Typography
            component="span"
            sx={{
              color: "#dc2626",
              fontWeight: 700,
              fontSize: "13px",
              lineHeight: 1.4,
            }}
          >
            *
          </Typography>
        ) : null}
      </Stack>
      {renderAddMenu(section)}
      {section === "description" && !hasText ? (
        <Typography
          variant="Footnote"
          sx={{
            color: "#dc2626",
            fontSize: "11px",
            fontWeight: 500,
            ml: 0.5,
            lineHeight: 1.4,
          }}
        >
          텍스트 컴포넌트가 필요합니다
        </Typography>
      ) : null}
    </Stack>
  );

  const renderDashedDivider = () => <Box sx={taskEditorDashedDividerSx} />;

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0}>
        {renderSectionHeader("description")}

        {/* description 배열 앞 drop zone */}
        {descCount > 0 ? (
          <Box
            data-testid="task-editor-component-drop-zone"
            data-drop-preview={isBeforePreviewActive ? "active" : "inactive"}
            onDragOver={(event) =>
              onComponentDragOver(event, {
                blockIndex,
                insertionIndex: 0,
              })
            }
            onDrop={(event) =>
              onComponentDrop(event, {
                blockIndex,
                insertionIndex: 0,
              })
            }
            sx={{
              ...taskEditorComponentDropZoneSx,
              height: isComponentDragActive
                ? isBeforePreviewActive
                  ? "24px"
                  : taskEditorComponentDropZoneSx.height
                : 0,
              opacity: isComponentDragActive ? 1 : 0,
              transform: isBeforePreviewActive ? "scaleY(1)" : "scaleY(0.92)",
            }}
          />
        ) : null}

        {block.description.map((component, descriptionIndex) => {
          const isDraggedComponent =
            draggedComponent?.blockIndex === blockIndex &&
            draggedComponent.descriptionIndex === descriptionIndex;
          const isFirstComponent = descriptionIndex === 0;
          const isFirstInSection = descriptionIndex === 0;
          const isLastInSection = descriptionIndex === descCount - 1;
          const sectionBg = "rgba(99, 102, 241, 0.05)";
          const sectionRadius = `${isFirstInSection ? "8px" : "0"} ${
            isFirstInSection ? "8px" : "0"
          } ${isLastInSection ? "8px" : "0"} ${isLastInSection ? "8px" : "0"}`;
          const isAfterComponentPreviewActive =
            isSameTaskEditorComponentDropPreview(componentDropPreview, {
              blockIndex,
              insertionIndex: descriptionIndex + 1,
            });

          return (
            <React.Fragment
              key={`description-${blockIndex}-${descriptionIndex}`}
            >
              <Sheet
                sx={{
                  ...taskEditorComponentCardSx,
                  px: 1.25,
                  py: 1.5,
                  backgroundColor: sectionBg,
                  borderRadius: sectionRadius,
                  borderBottom: isLastInSection
                    ? "none"
                    : `1px solid ${theme.vars.palette.Border.Weak}`,
                  opacity: isDraggedComponent ? 0.96 : 1,
                  transform: isDraggedComponent
                    ? "translateY(-6px) scale(1.005)"
                    : "none",
                  boxShadow: isDraggedComponent
                    ? "0 12px 28px rgba(15, 23, 42, 0.08)"
                    : taskEditorComponentCardSx.boxShadow,
                  zIndex: isDraggedComponent ? 2 : 1,
                }}
                data-testid="task-editor-component-card"
                data-section="description"
                data-description-index={descriptionIndex}
                data-dragging={isDraggedComponent ? "true" : "false"}
                onDragOver={(event) =>
                  props.onComponentCardDragOver(
                    event,
                    blockIndex,
                    descriptionIndex
                  )
                }
                onDrop={(event) =>
                  props.onComponentCardDrop(event, blockIndex, descriptionIndex)
                }
              >
                <Stack spacing={1.25}>
                  <DescriptionComponentHeader
                    blockIndex={blockIndex}
                    descriptionIndex={descriptionIndex}
                    component={component}
                    isFirstComponent={isFirstComponent}
                    isDragging={isDraggedComponent}
                    onDragStart={(event) =>
                      props.onComponentDragStart(
                        event,
                        blockIndex,
                        descriptionIndex
                      )
                    }
                    onDragEnd={props.onComponentDragEnd}
                    onCategoryChange={(nextType) =>
                      props.onDescriptionComponentTypeChange(
                        blockIndex,
                        descriptionIndex,
                        nextType
                      )
                    }
                    onDelete={() =>
                      props.onRemoveComponent({
                        blockIndex,
                        section: "description",
                        descriptionIndex,
                      })
                    }
                  />

                  <DescriptionComponentEditor
                    blockIndex={blockIndex}
                    descriptionIndex={descriptionIndex}
                    component={component}
                    onContentChange={props.onDescriptionContentChange}
                    onTypographyStyleChange={props.onTypographyStyleChange}
                    onReferencePresetTextChange={
                      props.onReferencePresetTextChange
                    }
                    onSubtypeChange={(nextType) =>
                      props.onDescriptionComponentTypeChange(
                        blockIndex,
                        descriptionIndex,
                        nextType
                      )
                    }
                    onImageFileChange={props.onImageFileChange}
                    onTextEditBlur={props.onTextEditBlur}
                  />
                </Stack>
              </Sheet>

              {/* 컴포넌트 이후 drop zone */}
              <Box
                data-testid="task-editor-component-drop-zone"
                data-drop-preview={
                  isAfterComponentPreviewActive ? "active" : "inactive"
                }
                onDragOver={(event) =>
                  onComponentDragOver(event, {
                    blockIndex,
                    insertionIndex: descriptionIndex + 1,
                  })
                }
                onDrop={(event) =>
                  onComponentDrop(event, {
                    blockIndex,
                    insertionIndex: descriptionIndex + 1,
                  })
                }
                sx={{
                  ...taskEditorComponentDropZoneSx,
                  height: isComponentDragActive
                    ? isAfterComponentPreviewActive
                      ? "24px"
                      : taskEditorComponentDropZoneSx.height
                    : 0,
                  opacity: isComponentDragActive ? 1 : 0,
                  transform: isAfterComponentPreviewActive
                    ? "scaleY(1)"
                    : "scaleY(0.92)",
                }}
              />
            </React.Fragment>
          );
        })}
      </Stack>

      {renderDashedDivider()}
      <Stack spacing={0}>
        {renderSectionHeader("action")}

        {block.action ? (
          <Sheet
            // description 카드와 동일한 spacing/정렬을 유지하되, 시각 분리를
            // 위해 옅은 배경 tint 와 둥근 모서리만 다시 살림.
            sx={{
              ...taskEditorComponentCardSx,
              px: 1,
              py: 0.75,
              backgroundColor: "rgba(100, 116, 139, 0.05)",
              borderRadius: "8px",
            }}
            data-testid="task-editor-component-card"
            data-section="action"
          >
            <Stack spacing={1.25}>
              <ActionComponentHeader
                blockIndex={blockIndex}
                actionType={block.action.type}
                onTypeChange={(nextType) =>
                  props.onActionComponentTypeChange(blockIndex, nextType)
                }
                onDelete={() =>
                  props.onRemoveComponent({
                    blockIndex,
                    section: "action",
                  })
                }
              />
              <ActionComponentEditor
                blockIndex={blockIndex}
                blockCount={props.blockCount}
                action={block.action}
                onSubtypeChange={props.onActionComponentTypeChange}
                onAddTextInputField={props.onAddTextInputField}
                onRemoveTextInputField={props.onRemoveTextInputField}
                onTextInputFieldChange={props.onTextInputFieldChange}
                onAddButtonOption={props.onAddButtonOption}
                onRemoveButtonOption={props.onRemoveButtonOption}
                onButtonOptionTextChange={props.onButtonOptionTextChange}
                onButtonQuizOptionCorrectToggle={
                  props.onButtonQuizOptionCorrectToggle
                }
                onButtonBranchOptionSequenceChange={
                  props.onButtonBranchOptionSequenceChange
                }
                onButtonQuizIncorrectMessageChange={
                  props.onButtonQuizIncorrectMessageChange
                }
                onButtonDefaultAllowMultipleChange={
                  props.onButtonDefaultAllowMultipleChange
                }
                onTextEditBlur={props.onTextEditBlur}
              />
            </Stack>
          </Sheet>
        ) : null}
      </Stack>
    </Stack>
  );
}

// ─── description 컴포넌트 헤더 ────────────────────────────────────────────

interface DescriptionComponentHeaderProps {
  blockIndex: number;
  descriptionIndex: number;
  component: TaskEditorDescriptionComponent;
  isFirstComponent: boolean;
  isDragging: boolean;
  onDragStart: (event: React.DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
  onCategoryChange: (nextType: TaskEditorDescriptionComponentType) => void;
  onDelete: () => void;
}

function DescriptionComponentHeader({
  blockIndex,
  descriptionIndex,
  component,
  isFirstComponent,
  isDragging,
  onDragStart,
  onDragEnd,
  onCategoryChange,
  onDelete,
}: DescriptionComponentHeaderProps) {
  const currentCategory = getTaskEditorComponentCategory(component.type);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ minWidth: 0 }}
      >
        {isFirstComponent ? (
          <Typography
            variant="Footnote"
            sx={{
              color: theme.vars.palette.Content.Weak,
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            텍스트
          </Typography>
        ) : (
          <IconButton
            size="sm"
            aria-label="컴포넌트 이동"
            data-testid={`task-editor-drag-component-${blockIndex}-${descriptionIndex}`}
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            sx={{
              cursor: isDragging ? "grabbing" : "grab",
            }}
          >
            <span
              style={{ fontSize: "18px" }}
              className="material-symbols-rounded"
            >
              drag_indicator
            </span>
          </IconButton>
        )}
        {isFirstComponent ? null : (
          <Select<TaskEditorComponentCategory>
            size="sm"
            value={currentCategory}
            onChange={(_, value) => {
              if (!value || value === currentCategory) return;
              const next = getTaskEditorCategoryDefaultType(value);
              if (isTaskEditorDescriptionComponentType(next)) {
                onCategoryChange(next);
              }
            }}
            sx={{
              ...taskEditorSelectSx,
              minWidth: { xs: "100%", sm: 140 },
            }}
            slotProps={{
              button: {
                "data-testid": `task-editor-element-type-select-${blockIndex}-${descriptionIndex}`,
                sx: taskEditorSelectButtonSx,
              },
              listbox: { sx: taskEditorSelectListboxSx },
            }}
          >
            {DESCRIPTION_CATEGORIES.map((category) => (
              <Option key={category} value={category}>
                {getTaskEditorCategoryLabel(category)}
              </Option>
            ))}
          </Select>
        )}
      </Stack>
      {isFirstComponent ? null : (
        <IconButton
          size="sm"
          aria-label="컴포넌트 삭제"
          data-testid={`task-editor-delete-component-${blockIndex}-${descriptionIndex}`}
          onClick={onDelete}
        >
          <span
            style={{ fontSize: "16px" }}
            className="material-symbols-rounded"
          >
            close
          </span>
        </IconButton>
      )}
    </Stack>
  );
}

// ─── description 본문 ─────────────────────────────────────────────────────

interface DescriptionComponentEditorProps {
  blockIndex: number;
  descriptionIndex: number;
  component: TaskEditorDescriptionComponent;
  onContentChange: (
    blockIndex: number,
    descriptionIndex: number,
    content: string,
    groupKey: string
  ) => void;
  onTypographyStyleChange: (
    blockIndex: number,
    descriptionIndex: number,
    style: TaskEditorTypographyStyle
  ) => void;
  onReferencePresetTextChange: (
    blockIndex: number,
    descriptionIndex: number,
    text: string
  ) => void;
  onSubtypeChange: (nextType: TaskEditorDescriptionComponentType) => void;
  onImageFileChange: (
    blockIndex: number,
    descriptionIndex: number,
    file: File
  ) => Promise<void>;
  onTextEditBlur: () => void;
}

const DescriptionComponentEditor = React.memo(
  DescriptionComponentEditorImpl,
  descriptionComponentEditorPropsEqual
);

function descriptionComponentEditorPropsEqual(
  prev: DescriptionComponentEditorProps,
  next: DescriptionComponentEditorProps
): boolean {
  return (
    prev.component === next.component &&
    prev.blockIndex === next.blockIndex &&
    prev.descriptionIndex === next.descriptionIndex
  );
}

function DescriptionComponentEditorImpl({
  blockIndex,
  descriptionIndex,
  component,
  onContentChange,
  onTypographyStyleChange,
  onReferencePresetTextChange,
  onSubtypeChange,
  onImageFileChange,
  onTextEditBlur,
}: DescriptionComponentEditorProps) {
  if (component.type === "text") {
    return (
      <Stack
        spacing={0.5}
        sx={{ minWidth: 0 }}
        data-testid="task-editor-element-card"
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Select<TaskEditorTypographyStyle>
            size="sm"
            value={component.typography_style}
            onChange={(_, value) => {
              if (!value) return;
              onTypographyStyleChange(blockIndex, descriptionIndex, value);
            }}
            sx={{
              ...taskEditorSubtypeSelectSx,
              alignSelf: "flex-start",
              width: "fit-content",
              minWidth: 110,
              maxWidth: 160,
            }}
            slotProps={{
              button: {
                "data-testid": `task-editor-typography-style-select-${blockIndex}-${descriptionIndex}`,
                sx: taskEditorSubtypeSelectButtonSx,
              },
              listbox: { sx: taskEditorSelectListboxSx },
            }}
          >
            {TASK_EDITOR_TYPOGRAPHY_STYLES.map((styleType) => (
              <Option key={`typography-style-${styleType}`} value={styleType}>
                {getTaskEditorTypographyStyleLabel(styleType)}
              </Option>
            ))}
          </Select>
        </Stack>
        <Textarea
          minRows={2}
          placeholder="내용을 입력하세요."
          value={component.content}
          onChange={(event) =>
            onContentChange(
              blockIndex,
              descriptionIndex,
              event.target.value,
              `text-content:${blockIndex}:${descriptionIndex}`
            )
          }
          onBlur={onTextEditBlur}
        />
      </Stack>
    );
  }

  if (component.type === "image") {
    return (
      <Stack
        spacing={0.5}
        sx={{ minWidth: 0 }}
        data-testid="task-editor-element-card"
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button
            component="label"
            size="sm"
            variant="outlined"
            color="neutral"
            sx={{
              ...taskEditorInlineAddButtonSx,
              width: "fit-content",
              flexShrink: 0,
            }}
          >
            파일 선택
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={async (event) => {
                const inputElement = event.target as HTMLInputElement;
                const selectedFile = inputElement.files?.[0];

                if (!selectedFile) {
                  return;
                }

                await onImageFileChange(
                  blockIndex,
                  descriptionIndex,
                  selectedFile
                );
                inputElement.value = "";
              }}
            />
          </Button>
          <Input
            size="sm"
            sx={{ flex: 1 }}
            placeholder={
              isTaskEditorLocalImageDataUrl(component.content)
                ? "로컬 업로드 이미지"
                : "이미지 URL (data: 또는 https://...)"
            }
            value={
              isTaskEditorLocalImageDataUrl(component.content)
                ? "로컬에서 업로드한 이미지 (수정 불가)"
                : component.content
            }
            readOnly={isTaskEditorLocalImageDataUrl(component.content)}
            disabled={isTaskEditorLocalImageDataUrl(component.content)}
            onChange={(event) => {
              if (isTaskEditorLocalImageDataUrl(component.content)) {
                return;
              }
              onContentChange(
                blockIndex,
                descriptionIndex,
                event.target.value,
                `image-content:${blockIndex}:${descriptionIndex}`
              );
            }}
            onBlur={onTextEditBlur}
          />
        </Stack>
        {component.content.trim() && (
          <Box sx={{ width: "100%" }}>
            <img
              src={component.content}
              alt="task-editor-image-preview"
              style={taskEditorImagePreviewSx as React.CSSProperties}
            />
          </Box>
        )}
      </Stack>
    );
  }

  // reference_automatic_thought / reference_coach_preset
  return (
    <Stack
      spacing={0.75}
      sx={{ minWidth: 0 }}
      data-testid={`task-editor-reference-editor-${blockIndex}-${descriptionIndex}`}
    >
      <Select<TaskEditorDescriptionComponentType>
        size="sm"
        value={component.type}
        onChange={(_, value) => {
          if (!value || value === component.type) return;
          onSubtypeChange(value);
        }}
        sx={{
          ...taskEditorSubtypeSelectSx,
          alignSelf: "flex-start",
          width: "fit-content",
          minWidth: 130,
        }}
        slotProps={{
          button: {
            "data-testid": `task-editor-reference-subtype-select-${blockIndex}-${descriptionIndex}`,
            sx: taskEditorSubtypeSelectButtonSx,
          },
          listbox: { sx: taskEditorSelectListboxSx },
        }}
      >
        {REFERENCE_SUBTYPES.map((type) => (
          <Option key={type} value={type}>
            {getTaskEditorComponentTypeLabel(type)}
          </Option>
        ))}
      </Select>
      {component.type === "reference_coach_preset" ? (
        <Input
          size="sm"
          placeholder="플레이스홀더 (코치에게만 보임)"
          value={component.preset_text}
          data-testid={`task-editor-reference-preset-text-${blockIndex}-${descriptionIndex}`}
          onChange={(event) =>
            onReferencePresetTextChange(
              blockIndex,
              descriptionIndex,
              event.target.value
            )
          }
          onBlur={onTextEditBlur}
        />
      ) : (
        <Sheet
          variant="soft"
          sx={{
            px: 1.25,
            py: 1,
            borderRadius: "10px",
            border: "1px solid",
            borderColor: theme.vars.palette.Border.Weak,
            backgroundColor: theme.vars.palette.Background.Level1,
          }}
        >
          <Typography
            variant="Footnote"
            sx={{
              color: theme.vars.palette.Content.Weak,
              fontSize: "12px",
            }}
          >
            유저의 자동적 사고 (저장된 데이터) 가 이 위치에 표시됩니다.
          </Typography>
        </Sheet>
      )}
    </Stack>
  );
}

// ─── action 헤더 ─────────────────────────────────────────────────────────

interface ActionComponentHeaderProps {
  blockIndex: number;
  actionType: TaskEditorActionComponentType;
  onTypeChange: (nextType: TaskEditorActionComponentType) => void;
  onDelete: () => void;
}

function ActionComponentHeader({
  blockIndex,
  actionType,
  onTypeChange,
  onDelete,
}: ActionComponentHeaderProps) {
  // description 컴포넌트의 카테고리 selector 와 동일한 패턴.
  // 카테고리 (텍스트 입력 / 버튼) 를 변경하면 그 카테고리의 default type 으로 전환.
  // 버튼 안의 sub-type (기본/퀴즈/분기) 은 button group editor 가 자체 selector 로 처리.
  const currentCategory = getTaskEditorComponentCategory(actionType);
  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="center"
    >
      <Select<TaskEditorComponentCategory>
        size="sm"
        value={currentCategory}
        onChange={(_, value) => {
          if (!value || value === currentCategory) return;
          const next = getTaskEditorCategoryDefaultType(value);
          if (isTaskEditorActionComponentType(next)) {
            onTypeChange(next);
          }
        }}
        sx={{
          ...taskEditorSelectSx,
          minWidth: 140,
        }}
        slotProps={{
          button: {
            "data-testid": `task-editor-action-category-select-${blockIndex}`,
            sx: taskEditorSelectButtonSx,
          },
          listbox: { sx: taskEditorSelectListboxSx },
        }}
      >
        {ACTION_CATEGORIES.map((category) => (
          <Option key={category} value={category}>
            {getTaskEditorCategoryLabel(category)}
          </Option>
        ))}
      </Select>
      <IconButton
        size="sm"
        aria-label="컴포넌트 삭제"
        data-testid={`task-editor-delete-component-${blockIndex}-action`}
        onClick={onDelete}
      >
        <span style={{ fontSize: "16px" }} className="material-symbols-rounded">
          close
        </span>
      </IconButton>
    </Stack>
  );
}

// ─── action 본문 ─────────────────────────────────────────────────────────

interface ActionComponentEditorProps {
  blockIndex: number;
  blockCount: number;
  action: TaskEditorActionComponent;
  onSubtypeChange: (
    blockIndex: number,
    nextType: TaskEditorActionComponentType
  ) => void;
  onAddTextInputField: (blockIndex: number) => void;
  onRemoveTextInputField: (blockIndex: number, fieldIndex: number) => void;
  onTextInputFieldChange: (
    blockIndex: number,
    fieldIndex: number,
    key: "label" | "placeholder" | "help_text",
    value: string
  ) => void;
  onAddButtonOption: (blockIndex: number) => void;
  onRemoveButtonOption: (blockIndex: number, optionIndex: number) => void;
  onButtonOptionTextChange: (
    blockIndex: number,
    optionIndex: number,
    text: string
  ) => void;
  onButtonQuizOptionCorrectToggle: (
    blockIndex: number,
    optionIndex: number,
    isCorrect: boolean
  ) => void;
  onButtonBranchOptionSequenceChange: (
    blockIndex: number,
    optionIndex: number,
    key: "show_from" | "show_to" | "restart_from" | "hide_from",
    value: number | undefined
  ) => void;
  onButtonQuizIncorrectMessageChange: (
    blockIndex: number,
    message: string
  ) => void;
  onButtonDefaultAllowMultipleChange: (
    blockIndex: number,
    allow: boolean
  ) => void;
  onTextEditBlur: () => void;
}

const ActionComponentEditor = React.memo(
  ActionComponentEditorImpl,
  actionComponentEditorPropsEqual
);

function actionComponentEditorPropsEqual(
  prev: ActionComponentEditorProps,
  next: ActionComponentEditorProps
): boolean {
  return (
    prev.action === next.action &&
    prev.blockIndex === next.blockIndex &&
    prev.blockCount === next.blockCount
  );
}

function ActionComponentEditorImpl(props: ActionComponentEditorProps) {
  const {
    blockIndex,
    blockCount,
    action,
    onSubtypeChange,
    onAddTextInputField,
    onRemoveTextInputField,
    onTextInputFieldChange,
    onAddButtonOption,
    onRemoveButtonOption,
    onButtonOptionTextChange,
    onButtonQuizOptionCorrectToggle,
    onButtonBranchOptionSequenceChange,
    onButtonQuizIncorrectMessageChange,
    onButtonDefaultAllowMultipleChange,
    onTextEditBlur,
  } = props;

  if (action.type === "text_input") {
    return (
      <Stack
        spacing={0.5}
        sx={{ minWidth: 0 }}
        data-testid="task-editor-element-card"
      >
        <TextInputFieldsEditor
          blockIndex={blockIndex}
          fields={action.fields}
          onAddField={onAddTextInputField}
          onRemoveField={onRemoveTextInputField}
          onChangeField={onTextInputFieldChange}
          onTextEditBlur={onTextEditBlur}
        />
      </Stack>
    );
  }

  // button_default / button_quiz / button_branch
  const branchTargetSequences = getTaskEditorBranchTargetSequences(
    blockCount,
    blockIndex
  );

  const renderOptionRow = (
    option: {
      text: string;
      is_correct?: boolean;
      show_from?: number;
      show_to?: number;
      restart_from?: number;
    },
    optionIndex: number
  ) => {
    const getSequenceLabel = (sequence?: number) =>
      typeof sequence === "number" ? `블록 ${sequence}` : "지정 블록";

    const showFromSummary =
      typeof option.show_from === "number"
        ? getSequenceLabel(option.show_from)
        : "기본 흐름";
    const showToSummary =
      typeof option.show_to === "number"
        ? getSequenceLabel(option.show_to)
        : "시작 블록";
    const restartFromSummary =
      typeof option.restart_from === "number"
        ? getSequenceLabel(option.restart_from)
        : "기본 순서";
    const optionSummary = `${showFromSummary} ~ ${showToSummary} · 이후 ${restartFromSummary}`;

    return (
      <Stack
        key={`button-option-${blockIndex}-${optionIndex}`}
        spacing={0.5}
        sx={taskEditorButtonGroupOptionRowSx}
      >
        <Chip
          variant="soft"
          color="neutral"
          sx={taskEditorButtonGroupOptionChipSx}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Input
              size="sm"
              variant="plain"
              sx={taskEditorButtonGroupOptionInputSx}
              placeholder={`옵션 ${optionIndex + 1}`}
              value={option.text}
              data-testid={`task-editor-button-option-input-${blockIndex}-${optionIndex}`}
              onChange={(event) =>
                onButtonOptionTextChange(
                  blockIndex,
                  optionIndex,
                  event.target.value
                )
              }
              onBlur={onTextEditBlur}
            />
            {action.type === "button_quiz" ? (
              <Checkbox
                size="sm"
                checked={option.is_correct === true}
                label="정답"
                data-testid={`task-editor-quiz-option-correct-${blockIndex}-${optionIndex}`}
                onChange={(event) =>
                  onButtonQuizOptionCorrectToggle(
                    blockIndex,
                    optionIndex,
                    event.target.checked
                  )
                }
              />
            ) : null}
            <IconButton
              size="sm"
              variant="plain"
              aria-label={`옵션 ${optionIndex + 1} 삭제`}
              data-testid={`task-editor-button-option-remove-${blockIndex}-${optionIndex}`}
              onClick={() => onRemoveButtonOption(blockIndex, optionIndex)}
              sx={{
                minWidth: "24px",
                minHeight: "24px",
                borderRadius: "999px",
              }}
            >
              <span
                style={{ fontSize: "14px" }}
                className="material-symbols-rounded"
              >
                close
              </span>
            </IconButton>
          </Stack>
        </Chip>

        {action.type === "button_branch" ? (
          <>
            <Box sx={taskEditorButtonGroupBranchControlsSx}>
              <BranchSequenceSelect
                label="보이기 시작"
                value={option.show_from}
                placeholder="기본 흐름"
                blockIndex={blockIndex}
                optionIndex={optionIndex}
                fieldKey="show_from"
                targetSequences={branchTargetSequences}
                onChange={onButtonBranchOptionSequenceChange}
              />
              <BranchSequenceSelect
                label="보이기 종료"
                value={option.show_to}
                placeholder="시작 블록과 동일"
                blockIndex={blockIndex}
                optionIndex={optionIndex}
                fieldKey="show_to"
                targetSequences={branchTargetSequences}
                onChange={onButtonBranchOptionSequenceChange}
              />
              <BranchSequenceSelect
                label="다시 시작"
                value={option.restart_from}
                placeholder="기본 순서"
                blockIndex={blockIndex}
                optionIndex={optionIndex}
                fieldKey="restart_from"
                targetSequences={branchTargetSequences}
                onChange={onButtonBranchOptionSequenceChange}
              />
            </Box>
            <Typography variant="Footnote" sx={taskEditorButtonGroupSummarySx}>
              {optionSummary}
            </Typography>
          </>
        ) : null}
      </Stack>
    );
  };

  return (
    <Box
      sx={taskEditorButtonGroupEditorSx}
      data-testid={
        action.type === "button_quiz"
          ? "task-editor-quiz-editor"
          : action.type === "button_default"
            ? "task-editor-button-selection-editor"
            : "task-editor-button-group-editor"
      }
    >
      <Select<TaskEditorActionComponentType>
        size="sm"
        value={action.type}
        onChange={(_, value) => {
          if (!value || value === action.type) return;
          onSubtypeChange(blockIndex, value);
        }}
        sx={{
          ...taskEditorSubtypeSelectSx,
          alignSelf: "flex-start",
          width: "fit-content",
          minWidth: 110,
        }}
        slotProps={{
          button: {
            "data-testid": `task-editor-button-subtype-select-${blockIndex}`,
            sx: taskEditorSubtypeSelectButtonSx,
          },
          listbox: { sx: taskEditorSelectListboxSx },
        }}
      >
        {BUTTON_SUBTYPES.map((type) => (
          <Option key={type} value={type}>
            {getTaskEditorComponentTypeLabel(type)}
          </Option>
        ))}
      </Select>

      <Stack spacing={0.5} sx={taskEditorCompactFieldSx}>
        <Box sx={taskEditorButtonGroupGuideTriggerSx}>
          <Typography variant="Footnote" sx={taskEditorCompactFieldLabelSx}>
            옵션
          </Typography>
          {action.type === "button_branch" ? (
            <Tooltip
              arrow
              placement="top-start"
              sx={taskEditorButtonGroupGuideTooltipSlotSx}
              slotProps={{
                arrow: { sx: taskEditorButtonGroupGuideTooltipArrowSx },
              }}
              title={
                <Typography
                  component="div"
                  sx={taskEditorButtonGroupGuideTooltipSx}
                >
                  {TASK_EDITOR_BUTTON_BRANCH_GUIDE_TEXT}
                </Typography>
              }
            >
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                aria-label="옵션 분기 가이드"
                sx={taskEditorButtonGroupGuideIconSx}
              >
                <span
                  style={{ fontSize: "14px" }}
                  className="material-symbols-rounded"
                >
                  help
                </span>
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Box sx={taskEditorButtonGroupOptionListSx}>
          {action.options.map((option, optionIndex) =>
            renderOptionRow(option, optionIndex)
          )}

          {action.type === "button_default" ? (
            <Stack spacing={0.5}>
              <Checkbox
                size="sm"
                checked={action.allow_multiple_selection}
                label="복수 선택 허용"
                data-testid={`task-editor-button-default-allow-multiple-${blockIndex}`}
                onChange={(event) =>
                  onButtonDefaultAllowMultipleChange(
                    blockIndex,
                    event.target.checked
                  )
                }
              />
            </Stack>
          ) : null}

          {action.type === "button_quiz" ? (
            <Input
              size="sm"
              placeholder="오답 문구 (예: 다시 생각해보세요)"
              value={action.incorrect_message}
              data-testid={`task-editor-button-quiz-incorrect-message-${blockIndex}`}
              onChange={(event) =>
                onButtonQuizIncorrectMessageChange(
                  blockIndex,
                  event.target.value
                )
              }
              onBlur={onTextEditBlur}
            />
          ) : null}

          <Button
            size="sm"
            variant="outlined"
            color="neutral"
            data-testid={`task-editor-button-add-option-${blockIndex}`}
            onClick={() => onAddButtonOption(blockIndex)}
            sx={taskEditorInlineAddButtonSx}
          >
            + 옵션 추가
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

// ─── 분기 sequence Select ─────────────────────────────────────────────────

interface BranchSequenceSelectProps {
  label: string;
  value: number | undefined;
  placeholder: string;
  blockIndex: number;
  optionIndex: number;
  fieldKey: "show_from" | "show_to" | "restart_from" | "hide_from";
  targetSequences: number[];
  onChange: (
    blockIndex: number,
    optionIndex: number,
    key: "show_from" | "show_to" | "restart_from" | "hide_from",
    value: number | undefined
  ) => void;
}

const BranchSequenceSelect = React.memo(BranchSequenceSelectImpl);

function BranchSequenceSelectImpl({
  label,
  value,
  placeholder,
  blockIndex,
  optionIndex,
  fieldKey,
  targetSequences,
  onChange,
}: BranchSequenceSelectProps) {
  return (
    <Stack sx={taskEditorButtonGroupBranchFieldSx}>
      <Typography
        variant="Footnote"
        sx={taskEditorButtonGroupBranchFieldLabelSx}
      >
        {label}
      </Typography>
      <Select<number | "">
        size="sm"
        value={typeof value === "number" ? value : ""}
        data-testid={`task-editor-button-branch-option-${fieldKey}-${blockIndex}-${optionIndex}`}
        onChange={(_, nextValue) => {
          if (
            nextValue === "" ||
            nextValue === null ||
            nextValue === undefined
          ) {
            onChange(blockIndex, optionIndex, fieldKey, undefined);
            return;
          }
          onChange(blockIndex, optionIndex, fieldKey, Number(nextValue));
        }}
        slotProps={{
          button: { sx: taskEditorButtonGroupBranchSelectSx },
          listbox: { sx: taskEditorSelectListboxSx },
        }}
      >
        <Option value="">{placeholder}</Option>
        {targetSequences.map((sequence) => (
          <Option
            key={`branch-${fieldKey}-${blockIndex}-${optionIndex}-${sequence}`}
            value={sequence}
          >
            {`블록 ${sequence}`}
          </Option>
        ))}
      </Select>
    </Stack>
  );
}

// ─── 텍스트 입력 필드 편집기 ──────────────────────────────────────────────

interface TextInputFieldsEditorProps {
  blockIndex: number;
  fields: { label?: string; placeholder?: string; help_text?: string }[];
  onAddField: (blockIndex: number) => void;
  onRemoveField: (blockIndex: number, fieldIndex: number) => void;
  onChangeField: (
    blockIndex: number,
    fieldIndex: number,
    key: "label" | "placeholder" | "help_text",
    value: string
  ) => void;
  onTextEditBlur: () => void;
}

const textInputFieldRows: {
  key: "label" | "placeholder" | "help_text";
  label: string;
  placeholder: string;
}[] = [
  { key: "label", label: "라벨", placeholder: "예: 오늘의 소감" },
  {
    key: "placeholder",
    label: "플레이스홀더",
    placeholder: "예: 소감을 남겨보세요",
  },
  {
    key: "help_text",
    label: "도움말",
    placeholder: "예: 자유롭게 적어도 됩니다",
  },
];

function TextInputFieldsEditor({
  blockIndex,
  fields,
  onAddField,
  onRemoveField,
  onChangeField,
  onTextEditBlur,
}: TextInputFieldsEditorProps) {
  const canRemove = fields.length > 1;

  return (
    <Stack spacing={1}>
      {fields.map((field, fieldIndex) => (
        <Sheet
          key={`text-input-field-${blockIndex}-${fieldIndex}`}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            border: "1px solid",
            borderColor: theme.vars.palette.Border.Weak,
            backgroundColor: theme.vars.palette.Surface.Forward,
            px: 1,
            py: 1,
          }}
          data-testid={`task-editor-text-input-field-${blockIndex}-${fieldIndex}`}
        >
          <Stack spacing={0.75}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                variant="Footnote"
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Weak,
                  letterSpacing: "0.02em",
                }}
              >
                {`인풋 ${fieldIndex + 1}`}
              </Typography>
              {canRemove ? (
                <IconButton
                  size="sm"
                  aria-label="인풋 삭제"
                  data-testid={`task-editor-remove-text-input-field-${blockIndex}-${fieldIndex}`}
                  onClick={() => onRemoveField(blockIndex, fieldIndex)}
                  sx={{ minHeight: "24px", minWidth: "24px" }}
                >
                  <span
                    style={{ fontSize: "14px" }}
                    className="material-symbols-rounded"
                  >
                    close
                  </span>
                </IconButton>
              ) : null}
            </Stack>
            {textInputFieldRows.map((row) => (
              <Stack key={row.key} spacing={0.25}>
                <Typography
                  variant="Footnote"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color: theme.vars.palette.Content.Medium,
                  }}
                >
                  {row.label}
                </Typography>
                <Input
                  size="sm"
                  placeholder={row.placeholder}
                  value={field[row.key] ?? ""}
                  data-testid={`task-editor-text-input-field-input-${blockIndex}-${fieldIndex}-${row.key}`}
                  onChange={(event) =>
                    onChangeField(
                      blockIndex,
                      fieldIndex,
                      row.key,
                      event.target.value
                    )
                  }
                  onBlur={onTextEditBlur}
                />
              </Stack>
            ))}
          </Stack>
        </Sheet>
      ))}
      <Button
        size="sm"
        variant="outlined"
        color="neutral"
        sx={{
          ...taskEditorInlineAddButtonSx,
          alignSelf: "flex-start",
        }}
        data-testid={`task-editor-add-text-input-field-${blockIndex}`}
        onClick={() => onAddField(blockIndex)}
      >
        + 인풋 추가
      </Button>
    </Stack>
  );
}

// ─── 블록 JSON 보기 모달 ──────────────────────────────────────────────────

interface TaskEditorBlockJsonModalProps {
  target: { block: TaskEditorBlock; sequence: number } | null;
  onClose: () => void;
}

function TaskEditorBlockJsonModal({
  target,
  onClose,
}: TaskEditorBlockJsonModalProps) {
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");
  useEffect(() => {
    if (!target) setCopyState("idle");
  }, [target]);
  const json = target ? JSON.stringify(target.block, null, 2) : "";
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1500);
    } catch {
      // 클립보드 권한 없으면 조용히 무시
    }
  };
  return (
    <Modal open={Boolean(target)} onClose={onClose}>
      <ModalDialog
        size="md"
        sx={{
          maxWidth: 720,
          width: "92vw",
          maxHeight: "90vh",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ModalClose />
        <Typography
          level="title-lg"
          sx={{
            fontWeight: 700,
            fontSize: "16px",
            color: theme.vars.palette.Content.Strong,
            mb: 0.25,
          }}
        >
          {target ? `블록 ${target.sequence}` : "블록"}
        </Typography>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Weak,
            fontSize: "12px",
            mb: 1.5,
          }}
        >
          이 블록의 description / action 이 어떻게 저장되는지 확인하세요.
        </Typography>
        <Box
          component="pre"
          sx={{
            flex: 1,
            overflow: "auto",
            backgroundColor: theme.vars.palette.Background.Level1,
            border: "1px solid",
            borderColor: theme.vars.palette.Border.Weak,
            borderRadius: "8px",
            p: 1.5,
            m: 0,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "12px",
            lineHeight: 1.55,
            color: theme.vars.palette.Content.Strong,
            whiteSpace: "pre",
            tabSize: 2,
          }}
          data-testid="task-editor-block-json"
        >
          {json}
        </Box>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 1.5 }}
        >
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            onClick={handleCopy}
            data-testid="task-editor-block-json-copy"
          >
            {copyState === "copied" ? "복사됨!" : "JSON 복사"}
          </Button>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}

// ─── 구조 도움말 모달 ─────────────────────────────────────────────────────

interface TaskEditorHelpModalProps {
  open: boolean;
  onClose: () => void;
}

const helpSectionBoxSx = {
  border: "1px dashed",
  borderColor: theme.vars.palette.Border.Weak,
  borderRadius: "10px",
  px: 1.5,
  py: 1.25,
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
} as const;

const helpCellBoxSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.5,
  borderRadius: "6px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid",
  borderColor: theme.vars.palette.Border.Weak,
  backgroundColor: theme.vars.palette.Surface.Forward,
  color: theme.vars.palette.Content.Strong,
  width: "fit-content",
} as const;

function TaskEditorHelpModal({ open, onClose }: TaskEditorHelpModalProps) {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        size="md"
        sx={{
          maxWidth: 560,
          width: "92vw",
          maxHeight: "90vh",
          overflow: "auto",
          p: 2.5,
        }}
      >
        <ModalClose />
        <Typography
          level="title-lg"
          sx={{
            fontWeight: 700,
            fontSize: "16px",
            color: theme.vars.palette.Content.Strong,
            mb: 0.25,
          }}
        >
          활동지 구조 알아보기
        </Typography>
        <Typography
          variant="Footnote"
          sx={{
            color: theme.vars.palette.Content.Weak,
            fontSize: "12px",
            mb: 2,
          }}
        >
          활동지는 여러 블록의 연속입니다. 각 블록은 아래 구조를 따릅니다.
        </Typography>

        <Sheet
          variant="outlined"
          sx={{
            borderRadius: "12px",
            p: 1.5,
            mb: 2,
            backgroundColor: theme.vars.palette.Background.Level1,
            borderColor: theme.vars.palette.Border.Weak,
          }}
        >
          <Typography
            variant="Footnote"
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: theme.vars.palette.Content.Medium,
              mb: 1.25,
              letterSpacing: "0.04em",
            }}
          >
            📦 블록
          </Typography>

          <Box sx={helpSectionBoxSx}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography
                variant="Footnote"
                sx={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                설명 (description)
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: "#dc2626",
                  fontWeight: 700,
                  fontSize: "13px",
                  lineHeight: 1.4,
                }}
              >
                *
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              <Box sx={helpCellBoxSx}>텍스트</Box>
              <Box sx={helpCellBoxSx}>이미지</Box>
              <Box sx={helpCellBoxSx}>불러오기</Box>
            </Stack>
          </Box>

          <Box
            sx={{
              borderTop: "1px dashed",
              borderColor: theme.vars.palette.Border.Weak,
              my: 1.25,
            }}
          />

          <Box sx={helpSectionBoxSx}>
            <Typography
              variant="Footnote"
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              유저 액션 (action, 단일 또는 null)
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap">
              <Box sx={helpCellBoxSx}>텍스트 입력</Box>
              <Box sx={helpCellBoxSx}>버튼</Box>
            </Stack>
          </Box>
        </Sheet>

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography
            variant="Footnote"
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: theme.vars.palette.Content.Medium,
              letterSpacing: "0.04em",
              mb: 0.25,
            }}
          >
            🧩 컴포넌트
          </Typography>
          {[
            { name: "텍스트", desc: "본문·제목·라벨 등 모든 종류의 문구" },
            { name: "이미지", desc: "활동지에 삽입할 이미지" },
            {
              name: "불러오기",
              desc: "자동적사고·코치 프리셋 등 외부 콘텐츠를 활동지에 보여줌",
            },
            {
              name: "텍스트 입력",
              desc: "유저가 자유롭게 텍스트를 입력하는 칸",
            },
            { name: "버튼", desc: "유저가 선택·분기·정답 확인 등을 하는 버튼" },
          ].map(({ name, desc }) => (
            <Stack
              key={name}
              direction="row"
              spacing={1}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  ...helpCellBoxSx,
                  flexShrink: 0,
                  minWidth: 80,
                  justifyContent: "center",
                }}
              >
                {name}
              </Box>
              <Typography
                variant="Footnote"
                sx={{
                  fontSize: "12px",
                  color: theme.vars.palette.Content.Medium,
                  lineHeight: 1.55,
                  pt: 0.5,
                }}
              >
                {desc}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack spacing={0.75}>
          <Typography
            variant="Footnote"
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: theme.vars.palette.Content.Medium,
              letterSpacing: "0.04em",
              mb: 0.25,
            }}
          >
            💡 작성 가이드
          </Typography>
          {[
            "설명 (description): 배열, 최소 1 개, text 포함 필수",
            "유저 액션 (action): 단일 객체 또는 null",
            "description[0] 은 항상 text 고정 — 삭제·이동 불가",
          ].map((tip, idx) => (
            <Stack
              key={idx}
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
            >
              <Box
                sx={{
                  mt: "5px",
                  width: 4,
                  height: 4,
                  borderRadius: "999px",
                  backgroundColor: theme.vars.palette.Content.Weak,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="Footnote"
                sx={{
                  fontSize: "12px",
                  color: theme.vars.palette.Content.Medium,
                  lineHeight: 1.55,
                }}
              >
                {tip}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
